/**
 * InstallPrompt — invito ad aggiungere Nullum alla home del telefono (PWA).
 *
 * - Android / Chrome desktop: intercetta `beforeinstallprompt` e mostra un
 *   bottone "Installa" che apre il prompt nativo.
 * - iOS Safari: non espone l'evento → apre una guida VISUALE (IosInstallSheet)
 *   con le icone reali e la freccia al pulsante Condividi.
 * - iOS dentro una webview in-app (WhatsApp/Instagram/...) o browser non-Safari:
 *   la stessa guida spiega come "Aprire in Safari" (lì non si può installare).
 * - Non compare se l'app è già installata (standalone) o se l'utente ha chiuso
 *   il banner.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { installContext } from '../lib/platform';
import IosInstallSheet from './IosInstallSheet';

const DISMISS_KEY = 'nullum.installPrompt.dismissed.v1';

export default function InstallPrompt() {
  const navigate = useNavigate();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    const c = installContext();
    setCtx(c);
    if (c.standalone) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    // Android / Chrome desktop
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS: nessun evento → mostra il banner dopo un attimo
    let t;
    if (c.ios) {
      t = setTimeout(() => setShow(true), 1200);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (t) clearTimeout(t);
    };
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  }

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    dismiss();
  }

  if (!ctx || !show) {
    // Lo sheet può restare aperto anche dopo che il banner è stato chiuso.
    return sheetOpen ? <IosInstallSheet onClose={() => setSheetOpen(false)} /> : null;
  }

  const needsSafari = ctx.iosNeedsSafari;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-50 p-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <div className="mx-auto max-w-md border border-ink bg-black/95 backdrop-blur p-3 text-[14px] text-ink-dim shadow-glow">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="text-ink text-glow mb-1">
                {needsSafari ? '🧭 Aprila in Safari per installarla' : '📲 Installa Nullum sul telefono'}
              </p>
              <p className="leading-relaxed">
                {ctx.ios
                  ? (needsSafari
                      ? `Sei dentro ${ctx.inApp || ctx.otherBrowser || 'un’altra app'}: da qui iPhone non può aggiungerla alla home. Ti mostro come passare a Safari.`
                      : 'Aggiungila alla home: si apre a schermo intero e ricevi le notifiche. Ti mostro come, è velocissimo.')
                  : 'Aggiungila alla home: si apre a schermo intero e ricevi le notifiche.'}
              </p>
            </div>
            <button
              onClick={dismiss}
              aria-label="chiudi"
              className="text-ink-dim hover:text-ink text-[18px] leading-none px-1"
            >
              ✕
            </button>
          </div>

          <div className="flex gap-2 mt-2">
            {ctx.ios ? (
              <button
                onClick={() => setSheetOpen(true)}
                className="flex-1 border border-ink bg-ink text-black font-mono text-[15px] py-2.5 px-3 tracking-wide"
              >
                {needsSafari ? '🧭 Come fare' : '📲 Mostrami come'}
              </button>
            ) : deferred ? (
              <button
                onClick={install}
                className="flex-1 border border-ink bg-ink text-black font-mono text-[15px] py-2.5 px-3 tracking-wide"
              >
                Installa ora
              </button>
            ) : (
              <button
                onClick={() => { setShow(false); navigate('/install'); }}
                className="flex-1 border border-ink-dim text-ink font-mono text-[14px] py-2.5 px-3 hover:border-ink"
              >
                🧭 Guidami passo passo
              </button>
            )}
          </div>
        </div>
      </div>

      {sheetOpen && <IosInstallSheet onClose={() => setSheetOpen(false)} />}
    </>
  );
}
